import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { BehaviorSubject, Subject } from 'rxjs';

export interface RecordingState {
  isRecording: boolean;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  recordButtonText: string;
  recordButtonDisabled: boolean;
  countdown: number;
}
@Injectable({
  providedIn: 'root'
})
export class VideoRecoderService {
  
 
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private videoStream: MediaStream | null = null;
  private audioStream: MediaStream | null = null;
  private combinedStream: MediaStream | null = null;

  
  public isRecording$ = new BehaviorSubject<boolean>(false);
  public recordedBlob$ = new Subject<Blob | null>();
  public previewStream$ = new BehaviorSubject<MediaStream | null>(null);
  public isVideoOn$ = new BehaviorSubject<boolean>(false);
  public isMicOn$ = new BehaviorSubject<boolean>(false);
  private isCancelled = false;

  constructor(private sanitizer: DomSanitizer) { }

  async toggleVideoStream() {
    if (this.videoStream) {
      this.stopStream(this.videoStream);
      this.videoStream = null;
      this.previewStream$.next(null);
      this.isVideoOn$.next(false);
    } else {
      try {
        this.videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        this.previewStream$.next(this.videoStream);
        this.isVideoOn$.next(true);
      } catch (error) {
        console.error('Error accessing video stream:', error);
      }
    }
  }

  async toggleAudioStream() {
    if (this.audioStream) {
      this.stopStream(this.audioStream);
      this.audioStream = null;
      this.isMicOn$.next(false);
    } else {
      try {
        this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.isMicOn$.next(true);
      } catch (error) {
        console.error('Error accessing audio stream:', error);
      }
    }
  }

  startRecording() {
    if (!this.isMicOn$.value || !this.isVideoOn$.value) {
      alert("Please turn on both your camera and microphone to start recording.");
      return;
    }

    try {
      this.chunks = [];
      this.combinedStream = new MediaStream([
        ...this.videoStream!.getTracks(),
        ...this.audioStream!.getTracks()
      ]);

      if (!MediaRecorder.isTypeSupported('video/webm')) {
        alert("Your browser doesn't support WebM format for recording.");
        return;
      }

      const options = { mimeType: 'video/webm;codecs=vp8,opus' };
      this.mediaRecorder = new MediaRecorder(this.combinedStream, options);

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        if(this.isCancelled){
          return;
        }
        const blob = new Blob(this.chunks, { type: "video/webm" });
        this.recordedBlob$.next(blob);
        this.isRecording$.next(false);
        
      
        this.stopStream(this.videoStream);
        this.stopStream(this.audioStream);
        this.stopStream(this.combinedStream);
        this.videoStream = null;
        this.audioStream = null;
        this.combinedStream = null;
        this.previewStream$.next(null);
        this.isVideoOn$.next(false);
        this.isMicOn$.next(false);
      };
    
      this.mediaRecorder.start();
      this.isRecording$.next(true);
    } catch (error) {
      console.error('Error starting MediaRecorder:', error);
      alert('Failed to start recording. Please try again.');
      this.isRecording$.next(false);
      this.stopStream(this.combinedStream);
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
     
      this.mediaRecorder.stop();
    }
  }

  cancelRecording() {
    this.isCancelled=true;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.isRecording$.next(false);
    this.stopStream(this.videoStream);
    this.stopStream(this.audioStream);
    this.stopStream(this.combinedStream);
    this.videoStream = null;
    this.audioStream = null;
    this.combinedStream = null;
    this.previewStream$.next(null);
    this.isVideoOn$.next(false);
    this.isMicOn$.next(false);
  
    this.chunks = [];
  
    this.recordedBlob$.next(null);
  }

  private stopStream(stream: MediaStream | null) {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }

}
